import React, { useContext, useState } from 'react'
import { AppContext } from 'contexts/AppContext';
import { Accordion, AccordionSummary, AccordionDetails, Box, useMediaQuery } from '@mui/material';
import CaretDown from 'components/icons/CaretDown';
import RoundIconButton1 from 'components/elements/RoundIconButton1';
import "./Order.css"
import { translate } from 'utils/Translate';
import ExplainerText from 'components/elements/ExplainerText';
import Headline6 from 'components/elements/Headline6';
import { priceConvertor } from 'utils/general';
import ContentNormal from 'components/elements/ContentNormal';
import Order from './Order';
import { callAPI } from 'utils/API';
import Image from 'components/elements/Image';
import ButtonMedium1 from 'components/elements/ButtonMedium1';
import ErrorModal from 'components/elements/ErrorModal';
import { Error } from '@mui/icons-material';
import { Modal } from 'react-bootstrap';
import Headline5 from 'components/elements/Headline5';
import ButtonMedium4 from 'components/elements/ButtonMedium4';
import CustomRadio from 'components/elements/CustomRadioButton';

const Confirmation = (props) => {
    const { styleGuide, userData, brandData, setUserData } = useContext(AppContext)
    const { confirmation, freightInfo, refresh } = props
    const [timelineEvents, setTimelineEvents] = useState([])
    const [isPaymentLoading, setIsPaymentLoading] = useState(false)
    const [payDeliveryWithCard, setPayDeliveryWithCard] = useState(false)
    const [showPaymentModal, setShowPaymentModal] = useState(false)
    const [error, setError] = useState('')
    const [alert, setAlert] = useState(confirmation.orders.some(o => o.alertCount)) // Order user remark has been updated
    const priceSuffix = brandData.isMerchandise ? translate("_DKK_", "kr") : translate("_POINTS_SHORT_", "pt")
    const isMobile = useMediaQuery('(max-width:900px)')
    const [deliveryIsUnpaid, setDeliveryIsUnpaid] = useState(confirmation.orderPaymentStatus === 'UNPAID_DELIVERY')

    const fetchOrderTimelineEvents = async () => {
        if(!timelineEvents.length){
            const orderIds = confirmation.orders.map(o => o.OrderID)
            const timelineEvents = await callAPI('/user/orders/timeline', 'GET', { query: { orderIds: orderIds } })
            if(timelineEvents && timelineEvents.length){
                setTimelineEvents(timelineEvents)
            }
        }
    }

    const removeAlerts = async () => {
        for (const o of confirmation.orders) {
            if (o.alertCount) {
                const updateAlertCount = await callAPI("order/updateOrderAlert", "POST", { body: { OrderID: o.OrderID } });
                if (updateAlertCount) setAlert(null)
            }
        }
    }

    const onPayClick = async () => {
        setIsPaymentLoading(true)
        setError('')

        if(payDeliveryWithCard){
            await onPayDeliveryWithCardClick()
        } else {
            await onPayDeliveryWithPointsClick()
        }

        setIsPaymentLoading(false)
    }

    const onPayDeliveryWithCardClick = async () => {
        const originUrl = window.location.origin
        const paymentSessionResponse = await callAPI('payment/deliveryPaymentSession', 'POST', { body: { callbackUrl: originUrl, ConfirmationID: confirmation.ConfirmationID } })

        if (paymentSessionResponse && paymentSessionResponse.redirectUrl) {
            window.location.href = paymentSessionResponse.redirectUrl;
        } else {
            setError(paymentSessionResponse.error || 'Kunne ikke oprette betaling for levering.')
        }
    }

    const onPayDeliveryWithPointsClick = async () => {
        const originUrl = window.location.origin
        const paymentSessionResponse = await callAPI('payment/payDelivery', 'POST', { body: { callbackUrl: originUrl, ConfirmationID: confirmation.ConfirmationID } })

        if (!paymentSessionResponse || paymentSessionResponse.error) {
            setError(paymentSessionResponse.error || 'Kunne ikke oprette betaling for levering.')
        } else {
            setDeliveryIsUnpaid(false)
            setShowPaymentModal(false)
            setUserData({
                ...userData,
                walletAmountPoints: userData?.walletAmountPoints - confirmation.totalFreightPoints,
            })
        }
    }

    const contactEmail = brandData?.contactEmail || 'support@upgrader.dk'
    const confirmationPrice = brandData?.isMerchandise ? confirmation.amountCurrency : confirmation.amountPoints

    return (
        <Box
            sx={{
                border: deliveryIsUnpaid ? `2px solid ${styleGuide.color12}` : 'none',
                borderRadius: 3,
                backgroundColor: styleGuide.color3
            }}
        >
            {deliveryIsUnpaid &&
                <Box sx={{display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', gap: 2, padding: 2}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <Error color={'error'} />
                        <ContentNormal>
                            Fragt er ikke betalt
                        </ContentNormal>
                    </Box>

                    <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2}}>
                        <ButtonMedium1
                            onClick={() => setShowPaymentModal(true)}
                            showIcon={false}
                            translationKey="_PROFILE_ORDER_DELIVERY_PAY_BUTTON_"
                            fallbackText="Betal nu"
                        />
                    </Box>
                </Box>
            }
            <Accordion id='orders-block' onClick={removeAlerts}>
                <AccordionSummary style={{padding: isMobile ? '4px 16px' : '20px 48px' }} expandIcon={<RoundIconButton1 icon={<CaretDown fill="white" />} />} onClick={fetchOrderTimelineEvents}>
                    <Box paddingY={isMobile ? 0 : 2} paddingRight={isMobile ? 1 : 4} display={'flex'} flex={1} gap={isMobile ? 2 : 4} justifyContent={'space-between'} flexWrap={'wrap'}>
                        <Box display={'flex'} gap={4}>
                            {alert && !isMobile && (
                                <Image style={{ width: 15, height: 30, padding: 10, position: 'absolute', left: 18 }} src={`url(https://i.imgur.com/8ZvWaVU.png)`} />
                            )}
                            <Box display={'flex'} flexDirection={'column'} gap={1} width={isMobile ? 100 : 120}>
                                <ExplainerText translationKey='_PROFILE_ORDER_CONFIRMATION_' fallbackText='Ordrebekræftelse' />
                                <Headline6 fallbackText={`${confirmation.ConfirmationID || '-'}`} />
                            </Box>
                            <Box display={'flex'} flexDirection={'column'} gap={1} width={isMobile ? 100 : 120}>
                                <ExplainerText translationKey='_PROFILE_ORDER_DATE_' fallbackText={'Ordredato'} />
                                <Headline6 fallbackText={`${confirmation.date || '-'}`} />
                            </Box>
                            {!!confirmation.shipmentReference &&
                                <Box display={'flex'} flexDirection={'column'} gap={1} width={isMobile ? 100 : 120}>
                                    <ExplainerText translationKey='_PROFILE_SHIPMENT_REFERENCE_' fallbackText={'Forsendelsesreference'} />
                                    <Headline6 fallbackText={confirmation.shipmentReference} />
                                </Box>
                            }
                        </Box>

                        {!brandData?.features?.disablePoints &&
                            <Box display={'flex'} columnGap={4} rowGap={2} flexWrap={'wrap'}>
                                {!!freightInfo?.enableFreightPriceInterval &&
                                    <Box display={'flex'} flexDirection={'column'} gap={1} width={isMobile ? 100 : 120}>
                                        <ExplainerText translationKey='_PROFILE_ORDER_POINTS_' fallbackText={'Ordre'} />
                                        <Headline6 fallbackText={priceConvertor(brandData, confirmationPrice, priceSuffix)} />
                                    </Box>
                                }

                                {!!freightInfo?.enableFreightPriceInterval &&
                                    <Box display={'flex'} flexDirection={'column'} gap={1} width={isMobile ? 100 : 120}>
                                        <ExplainerText translationKey='_PROFILE_ORDER_FREIGHT_' fallbackText={'Fragt'} />
                                        {confirmation.deliveryPaidWithCard
                                            ? <Headline6 fallbackText={priceConvertor(brandData, confirmation.totalFreightDKK, translate("_DKK_", "kr"))} />
                                            : <Headline6 fallbackText={priceConvertor(brandData, confirmation.totalFreightPoints, priceSuffix)} />
                                        }
                                    </Box>
                                }

                                {!confirmation.deliveryPaidWithCard &&
                                    <Box display={'flex'} flexDirection={'column'} gap={1} width={isMobile ? 100 : 120}>
                                        <ExplainerText translationKey='_PROFILE_ORDER_TOTAL_' fallbackText={'Total'} />
                                        <Headline6 fallbackText={priceConvertor(brandData, (confirmationPrice + confirmation.totalFreightPoints), priceSuffix)} />
                                    </Box>
                                }
                            </Box>
                        }
                    </Box>
                </AccordionSummary>
                <AccordionDetails style={{borderTop: `1px solid ${styleGuide.color4}`, padding: 0}} >
                    {confirmation.orders.sort((a, b) => a.OrderID - b.OrderID).map((o, i) => (
                        <Order key={o.OrderID} order={o} timelineEvents={timelineEvents.filter(e => e.parentRelationID === o.OrderID)} refresh={refresh} index={i+1} />
                    ))}

                    {/* Confirmation info boxes */}
                    <Box display={'flex'} flexWrap={'wrap'} gap={isMobile ? 2 : 6} padding={isMobile ? 2 : 6}>
                        <ConfirmationInfoBox styleGuide={styleGuide}>
                            <Headline6 style={{ marginBottom: 10 }} translationKey="_BASKET_OVERVIEW_DELIVERY_INFORMATION_CAPTION_" fallbackText="Leveringsoplysninger" />
                            <ContentNormal>
                                {confirmation.orderConsigneeName || userData?.userName}
                                <br />
                                {`${confirmation.orderConsigneeAddress1}, ${confirmation.orderConsigneeZipcode} ${confirmation.orderConsigneeCity}`}
                                <br />
                                {confirmation.orderConsigneeEmail || ''}
                                <br />
                                {!confirmation.orderConsigneePhone ? translate('_USER_PHONE_NOT_AVAILABLE_', 'Telefonnummer er ikke tilgængeligt') : confirmation.orderConsigneePhone}
                            </ContentNormal>
                        </ConfirmationInfoBox>

                        <ConfirmationInfoBox styleGuide={styleGuide}>
                            <Headline6 style={{ marginBottom: 10 }} translationKey="_PROFILE_TNT_INFO_HEADLINE_" fallbackText="Track & Trace eller Dropship" />
                            <ContentNormal translationKey="_PROFILE_TNT_INFO_" fallbackText="I ordrebekræftelsen kan du tilgå track & trace på varer der afsendes fra vores lager. Ved dropship vil du i stedet modtage informationerne direkte fra vores leverandør." />
                        </ConfirmationInfoBox>

                        <ConfirmationInfoBox styleGuide={styleGuide}>
                            <Headline6 style={{ marginBottom: 10 }} translationKey="_PROFILE_ORDER_CONTACT_HEADLINE_" fallbackText="Har du spørgsmål til din ordre?" />
                            <ContentNormal translationKey="_PROFILE_ORDER_CONTACT_INFO_" fallbackText="Du er altid velkommen til at kontakte os." />
                            <Box marginTop={2} display={'flex'} flexDirection={'column'} gap={1}>
                                <a href={`mailto:${contactEmail}`} style={{textDecoration: 'none'}}>
                                    <ContentNormal fallbackText={contactEmail} />
                                </a>
                                {brandData.contactPhone && <ContentNormal fallbackText={brandData.contactPhone} />}
                            </Box>
                        </ConfirmationInfoBox>
                    </Box>
                </AccordionDetails>
            </Accordion>

            <Modal
                show={showPaymentModal}
                onHide={() => setShowPaymentModal(false)}
                style={{ margin: "auto" }}
                centered
            >
                <Modal.Header
                    className="border-0"
                    closeButton
                    style={{ fontSize: 9 }}
                >
                    <Headline5
                        translationKey="_DELIVERY_PAYMENT_"
                        fallbackText="Betaling af fragt"
                    />
                </Modal.Header>
                <Modal.Body className="pt-0">
                    <Box>
                        <Box sx={{ display: 'flex', gap: 4 }}>
                            <CustomRadio
                                checked={!payDeliveryWithCard}
                                onChange={() => setPayDeliveryWithCard(false)}
                                label={
                                    <ContentNormal>
                                        {translate('_PROFILE_PAY_DELIVERY_WITH_POINTS_', 'Betal med xx point').replace('xx', confirmation.totalFreightPoints)}
                                    </ContentNormal>
                                }
                            />

                            <CustomRadio
                                checked={payDeliveryWithCard}
                                onChange={() => setPayDeliveryWithCard(true)}
                                label={
                                    <ContentNormal>
                                        {translate('_PROFILE_PAY_DELIVERY_WITH_CARD_', 'Betal med xx kroner').replace('xx', confirmation.totalFreightDKK)}
                                    </ContentNormal>
                                }
                            />
                        </Box>
                    </Box>
                </Modal.Body>
                <Modal.Footer>
                    <Box
                        display={'flex'}
                        justifyContent={'flex-end'}
                        gap={2}
                    >
                        <ButtonMedium1
                            showIcon={false}
                            translationKey="_CONFIRM_"
                            fallbackText="Bekræft"
                            onClick={() => onPayClick()}
                            loading={isPaymentLoading}
                        />
                        <ButtonMedium4
                            showIcon={false}
                            translationKey="_CLOSE_"
                            fallbackText="Fortryd"
                            onClick={() => setShowPaymentModal(false)}
                        />
                    </Box>
                </Modal.Footer>
            </Modal>
            
            <ErrorModal
                show={!!error}
                onHide={() => setError('')}
                headline={translate('_ERROR_OCCURRED_', 'Der opstod en fejl')}
            >
                <ContentNormal>
                    {error}
                </ContentNormal>
            </ErrorModal>

        </Box>
    )
}

const ConfirmationInfoBox = (props) => {
    const isMobile = useMediaQuery('(max-width:900px)')
    return (
        <Box
            flex={1}
            display={'flex'}
            flexDirection={'column'}
            backgroundColor={props.styleGuide.color2}
            padding={isMobile ? 3 : 4}
            paddingBottom={8}
            gap={1}
            borderRadius={'14px'}
            minWidth={250}
        >
            {props.children}
        </Box>
    )
}

export default Confirmation;